import { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  FileSearch,
  AlertTriangle,
  Settings,
  Activity,
  Shield
} from 'lucide-react'
import clsx from 'clsx'

interface DashboardLayoutProps {
  children: ReactNode
  realtimeEnabled: boolean
}

export default function DashboardLayout({ children, realtimeEnabled }: DashboardLayoutProps) {
  const location = useLocation()

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Scans', href: '/scans', icon: FileSearch },
    { name: 'Violations', href: '/violations', icon: AlertTriangle },
    { name: 'Settings', href: '/settings', icon: Settings },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation Bar */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-dii-primary mr-3" />
              <h1 className="text-xl font-bold text-gray-900">
                Governance Platform
              </h1>
              <span className="ml-4 text-sm text-gray-500">
                FMS Contract V4.1.0 Compliance Monitoring
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <Activity
                  className={clsx(
                    "h-4 w-4 mr-2",
                    realtimeEnabled ? "text-green-500 animate-pulse" : "text-gray-400"
                  )}
                />
                <span className="text-sm text-gray-600">
                  {realtimeEnabled ? 'Live' : 'Connecting...'}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                {new Date().toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex h-[calc(100vh-4rem)]">
        {/* Sidebar Navigation */}
        <nav className="w-64 bg-white shadow-sm border-r border-gray-200">
          <div className="flex flex-col h-full py-4">
            <div className="flex-1">
              <ul className="space-y-1 px-3">
                {navigation.map((item) => {
                  const Icon = item.icon
                  const isActive = location.pathname === item.href
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        className={clsx(
                          'flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                          isActive
                            ? 'bg-dii-primary text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        )}
                      >
                        <Icon className="h-5 w-5 mr-3" />
                        {item.name}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>

            {/* Quick Stats */}
            <div className="px-3 py-4 border-t border-gray-200">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Quick Stats
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Platform Score</span>
                  <span className="font-medium text-gray-900">82%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Apps Monitored</span>
                  <span className="font-medium text-gray-900">4</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Active Violations</span>
                  <span className="font-medium text-red-600">21</span>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}